import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  FormControlLabel,
  Grid2,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Popover,
  styled,
  Typography,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { PrimaryFormActionButton, PrimaryTooltip } from "../../../Components/StyledComponent";
import SearchInput from "./SearchInput";
import FilterSelectBox from "./FilterSelectBox";
import CustomCheckbox from "./Checkbox";
import { getSelectedLocationsDisplayLabelOutdated } from "../CommonFunctions";
import { useTranslation } from "react-i18next";

const popoverPaperStyles = {
  py: "20px",
  px: "16px",
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  boxShadow:
    "2px 2px 4px 0px rgba(151, 151, 151, 0.25), -2px -2px 4px 0px rgba(151, 151, 151, 0.25)",
};

const StyledListItem = styled(ListItem)(() => ({
  paddingLeft: 0,
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ hasChildren, isLastNode }) => ({
  marginLeft: hasChildren ? "6px" : isLastNode ? "calc(6px + 10px)" : "calc(6px + 22px)", // adding ExpandCollapseIcon size
}));

const ExpandCollapseIcon = styled(ArrowRightIcon)(({ isExpanded }) => ({
  transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
  fontSize: "22px",
}));

const deepClone = (v) => JSON.parse(JSON.stringify(v));

function filterLocations(data, query) {
  if (!query) return data;
  const q = query.toLowerCase();

  return data
    .map((country) => {
      const states = country.states
        .map((state) => {
          const cities = state.cities
            .map((city) => {
              const depotMatches = city.depots.filter((d) =>
                d.toLowerCase().includes(q)
              );

              if (city.name.toLowerCase().includes(q)) {
                // City matches → include city but *no depots* unless depot matches
                return { ...city, depots: depotMatches };
              } else if (depotMatches.length > 0) {
                // Only depot(s) match
                return { ...city, depots: depotMatches };
              }

              return null; // city doesn't match and no matching depots
            })
            .filter(Boolean);

          if (state.state.toLowerCase().includes(q) || cities.length > 0) {
            return { ...state, cities };
          }
          return null;
        })
        .filter(Boolean);

      if (country.country.toLowerCase().includes(q) || states.length > 0) {
        return { ...country, states };
      }
      return null;
    })
    .filter(Boolean);
}

/* Helper: Convert transformed format back to internal format */
const convertFromTransformedFormat = (transformedSelection, allLocations) => {
  if (!transformedSelection || transformedSelection.length === 0) {
    return deepClone(allLocations);
  }

  return transformedSelection.map(country => {
    const origCountry = allLocations.find(c => c.country === country.country);
    if (!origCountry) return { country: country.country, states: [] };

    // Optimization: If children missing/empty, it implies Full Selection (copy original)
    if (!country.children || country.children.length === 0) {
      return deepClone(origCountry);
    }

    const result = { country: country.country };
    result.states = country.children.map(state => {
      const origState = origCountry.states.find(s => s.state === state.state);
      if (!origState) return { state: state.state, cities: [] };

      if (!state.children || state.children.length === 0) {
        return deepClone(origState);
      }

      const stateResult = { state: state.state };
      stateResult.cities = state.children.map(city => {
        const origCity = origState.cities.find(c => c.name === city.city);
        if (!origCity) return { name: city.city, depots: [] };

        if (!city.children || city.children.length === 0) {
          return deepClone(origCity);
        }

        const cityResult = { name: city.city };
        cityResult.depots = city.children.map(depot => depot.depot);
        return cityResult;
      });
      return stateResult;
    });

    return result;
  });
};

/** Countries open by default, so the first level shows without a click. */
const buildDefaultExpanded = (locations = []) =>
  (locations || []).reduce((expanded, country) => {
    if (country?.country) expanded[country.country] = true;
    return expanded;
  }, {});

/* Component */
const OnboardingLocationFilter = ({ locations, onChange, selectedLocations = null }) => {
  const { t } = useTranslation(["common"]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState(() => buildDefaultExpanded(locations));
  const [selected, setSelected] = useState(() => {
    if (selectedLocations) {
      return convertFromTransformedFormat(selectedLocations, locations);
    }
    return deepClone(locations);
  });
  const [search, setSearch] = useState("");
  const [displayLabel, setDisplayLabel] = useState("All Countries");
  const [tooltipLabel, setTooltipLabel] = useState("All Countries");
  const [enableSelectAllOption, setEnableSelectAllOption] = useState(true);
  const [enableDeselectAllOption, setEnableDeselectAllOption] = useState(true);

  const open = Boolean(anchorEl);

  const toggleExpand = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  /* Helper: find original nodes */
  const findOriginalCountry = (countryName) =>
    locations.find((c) => c.country === countryName);
  const findOriginalState = (countryName, stateName) =>
    findOriginalCountry(countryName)?.states?.find(
      (s) => s.state === stateName
    );
  const findOriginalCity = (countryName, stateName, cityName) =>
    findOriginalState(countryName, stateName)?.cities?.find(
      (c) => c.name === cityName
    );

  /* ---------- Toggle functions (maintain the nested selected schema) ---------- */

  const toggleCountry = (countryName) =>
    setSelected((prev) => {
      const exists = prev.find((c) => c.country === countryName);
      if (exists) {
        return prev.filter((c) => c.country !== countryName);
      } else {
        const orig = findOriginalCountry(countryName);
        if (!orig) return prev;
        return [...prev, deepClone(orig)];
      }
    });

  const toggleState = (countryName, stateName) =>
    setSelected((prev) => {
      const copy = deepClone(prev);
      const countryIndex = copy.findIndex((c) => c.country === countryName);
      const origState = findOriginalState(countryName, stateName);
      if (countryIndex === -1) {
        // add country with this state only
        copy.push({ country: countryName, states: [deepClone(origState)] });
        return copy;
      }
      const countrySel = copy[countryIndex];
      countrySel.states = countrySel.states || [];
      const stateIndex = countrySel.states.findIndex(
        (s) => s.state === stateName
      );
      if (stateIndex > -1) {
        // remove state
        countrySel.states.splice(stateIndex, 1);
        if (!countrySel.states.length) {
          // remove country if no states left
          copy.splice(countryIndex, 1);
        }
      } else {
        // add state (full)
        countrySel.states.push(deepClone(origState));
      }
      return copy;
    });

  const toggleCity = (countryName, stateName, cityName) =>
    setSelected((prev) => {
      const copy = deepClone(prev);
      let countrySel = copy.find((c) => c.country === countryName);
      if (!countrySel) {
        // create country -> state -> city
        const origState = findOriginalState(countryName, stateName);
        const origCity = findOriginalCity(countryName, stateName, cityName);
        copy.push({
          country: countryName,
          states: [{ state: stateName, cities: [deepClone(origCity)] }],
        });
        return copy;
      }
      countrySel.states = countrySel.states || [];
      let stateSel = countrySel.states.find((s) => s.state === stateName);
      if (!stateSel) {
        // add state with this city
        const origCity = findOriginalCity(countryName, stateName, cityName);
        countrySel.states.push({
          state: stateName,
          cities: [deepClone(origCity)],
        });
        return copy;
      }
      stateSel.cities = stateSel.cities || [];
      const cityIndex = stateSel.cities.findIndex((c) => c.name === cityName);
      if (cityIndex > -1) {
        // remove city
        stateSel.cities.splice(cityIndex, 1);
        if (!stateSel.cities.length) {
          // remove state if empty
          countrySel.states = countrySel.states.filter(
            (s) => s.state !== stateName
          );
          if (!countrySel.states.length) {
            // remove country if empty
            const idx = copy.findIndex((c) => c.country === countryName);
            if (idx > -1) copy.splice(idx, 1);
          }
        }
      } else {
        const origCity = findOriginalCity(countryName, stateName, cityName);
        stateSel.cities.push(deepClone(origCity));
      }
      return copy;
    });

  const toggleDepot = (countryName, stateName, cityName, depotName) =>
    setSelected((prev) => {
      const copy = deepClone(prev);
      let countrySel = copy.find((c) => c.country === countryName);
      if (!countrySel) {
        // create full path with this depot
        copy.push({
          country: countryName,
          states: [
            {
              state: stateName,
              cities: [{ name: cityName, depots: [depotName] }],
            },
          ],
        });
        return copy;
      }
      countrySel.states = countrySel.states || [];
      let stateSel = countrySel.states.find((s) => s.state === stateName);
      if (!stateSel) {
        countrySel.states.push({
          state: stateName,
          cities: [{ name: cityName, depots: [depotName] }],
        });
        return copy;
      }
      stateSel.cities = stateSel.cities || [];
      let citySel = stateSel.cities.find((c) => c.name === cityName);
      if (!citySel) {
        stateSel.cities.push({ name: cityName, depots: [depotName] });
        return copy;
      }
      citySel.depots = citySel.depots || [];
      const depotIndex = citySel.depots.findIndex((d) => d === depotName);
      if (depotIndex > -1) {
        // remove depot
        citySel.depots.splice(depotIndex, 1);
        // cleanup if empty
        if (!citySel.depots.length) {
          stateSel.cities = stateSel.cities.filter((c) => c.name !== cityName);
          if (!stateSel.cities.length) {
            countrySel.states = countrySel.states.filter(
              (s) => s.state !== stateName
            );
            if (!countrySel.states.length) {
              // remove country if no states
              const idx = copy.findIndex((c) => c.country === countryName);
              if (idx > -1) copy.splice(idx, 1);
            }
          }
        }
      } else {
        citySel.depots.push(depotName);
      }
      return copy;
    });

  /* ---------- Selection-check helpers (compare original data with selected) ---------- */

  const findSelCountry = (countryName) =>
    selected.find((c) => c.country === countryName);
  const findSelState = (countryName, stateName) =>
    findSelCountry(countryName)?.states?.find((s) => s.state === stateName);
  const findSelCity = (countryName, stateName, cityName) =>
    findSelState(countryName, stateName)?.cities?.find(
      (c) => c.name === cityName
    );

  const isCityFullySelected = (countryName, stateName, city) => {
    const selCity = findSelCity(countryName, stateName, city.name);
    if (!selCity) return false;
    // all depots in original must be present in selCity.depots
    const origDepots = city.depots || [];
    return origDepots.every((d) => selCity.depots?.includes(d));
  };

  const isCityPartiallySelected = (countryName, stateName, city) => {
    const selCity = findSelCity(countryName, stateName, city.name);
    if (!selCity) return false;
    const origDepots = city.depots || [];
    // some but not all depots selected
    const selCount = selCity.depots?.length || 0;
    return selCount > 0 && !isCityFullySelected(countryName, stateName, city);
  };

  const isStateFullySelected = (countryName, state) => {
    const selState = findSelState(countryName, state.state);
    if (!selState) return false;
    // every city fully selected
    return state.cities.every((city) =>
      isCityFullySelected(countryName, state.state, city)
    );
  };

  const isStatePartiallySelected = (countryName, state) => {
    const selState = findSelState(countryName, state.state);
    if (!selState) return false;
    // any city partially/fully selected but not all fully
    const anySelected = state.cities.some(
      (city) =>
        isCityFullySelected(countryName, state.state, city) ||
        isCityPartiallySelected(countryName, state.state, city)
    );
    return anySelected && !isStateFullySelected(countryName, state);
  };

  const isCountryFullySelected = (country) => {
    const selCountry = findSelCountry(country.country);
    if (!selCountry) return false;
    // every state fully selected
    return country.states.every((s) =>
      isStateFullySelected(country.country, s)
    );
  };

  const isCountryPartiallySelected = (country) => {
    const selCountry = findSelCountry(country.country);
    if (!selCountry) return false;
    const anySelected = country.states.some(
      (s) =>
        isStateFullySelected(country.country, s) ||
        isStatePartiallySelected(country.country, s)
    );
    return anySelected && !isCountryFullySelected(country);
  };

  /* ---------- Render helpers ---------- */
  const renderDepots = (countryName, stateName, city) =>
    (city.depots || []).map((depot) => {
      const checked = !!findSelCity(
        countryName,
        stateName,
        city.name
      )?.depots?.includes(depot);
      return (
        <StyledListItem
          key={`${countryName}-${stateName}-${city.name}-${depot}`}
          dense
          sx={{ pl: 8 }}
        >
          <ListItemIcon sx={{ minWidth: "auto" }}>
            <StyledFormControlLabel
              hasChildren={false}
              isLastNode={true}
              control={
                <CustomCheckbox
                  edge="start"
                  checked={checked}
                  onChange={() =>
                    toggleDepot(countryName, stateName, city.name, depot)
                  }
                />
              }
              label={
                <Typography
                  variant="fw400s12c494949"
                  sx={{ lineHeight: "24px" }}
                >
                  {depot}
                </Typography>
              }
            />
          </ListItemIcon>
        </StyledListItem>
      );
    });

  const renderCities = (countryName, state) =>
    state.cities.map((city) => {
      const key = `${countryName}-${state.state}-${city.name}`;
      const checked = isCityFullySelected(countryName, state.state, city);
      const indeterminate = isCityPartiallySelected(
        countryName,
        state.state,
        city
      );

      return (
        <React.Fragment key={key}>
          <StyledListItem dense sx={{ pl: 4 }}>
            {city.depots.length > 0 && (
              <IconButton
                size="small"
                onClick={() => toggleExpand(key)}
                sx={{ p: 0 }}
              >
                <ExpandCollapseIcon isExpanded={expanded[key]} />
              </IconButton>
            )}
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <StyledFormControlLabel
                hasChildren={city.depots.length > 0}
                isLastNode={false}
                control={
                  <CustomCheckbox
                    edge="start"
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={() =>
                      toggleCity(countryName, state.state, city.name)
                    }
                  />
                }
                label={
                  <Typography
                    variant="fw400s12c494949"
                    sx={{ lineHeight: "24px" }}
                  >
                    {city.name}
                  </Typography>
                }
              />
            </ListItemIcon>
          </StyledListItem>

          {city.depots.length > 0 && (
            <Collapse in={expanded[key]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {renderDepots(countryName, state.state, city)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  const renderStates = (country) =>
    country.states.map((state) => {
      const key = `${country.country}-${state.state}`;
      const checked = isStateFullySelected(country.country, state);
      const indeterminate = isStatePartiallySelected(country.country, state);

      return (
        <React.Fragment key={key}>
          <StyledListItem dense sx={{ pl: 2 }}>
            {state.cities.length > 0 && (
              <IconButton
                size="small"
                onClick={() => toggleExpand(key)}
                sx={{ p: 0 }}
              >
                <ExpandCollapseIcon isExpanded={expanded[key]} />
              </IconButton>
            )}
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <StyledFormControlLabel
                hasChildren={state.cities.length > 0}
                isLastNode={false}
                control={
                  <CustomCheckbox
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={() => toggleState(country.country, state.state)}
                  />
                }
                label={
                  <Typography
                    variant="fw400s12c494949"
                    sx={{ lineHeight: "24px" }}
                  >
                    {state.state}
                  </Typography>
                }
              />
            </ListItemIcon>
          </StyledListItem>

          <Collapse in={expanded[key]} timeout="auto" unmountOnExit>
            <List disablePadding>{renderCities(country.country, state)}</List>
          </Collapse>
        </React.Fragment>
      );
    });

  /* ---------- Transform selection to desired output format ---------- */
  const transformSelection = (selected) => {
    return selected.map(country => {
      // Optimization: If country is fully selected, don't send children (implies all)
      const origCountry = findOriginalCountry(country.country);
      if (origCountry && isCountryFullySelected(origCountry)) {
        return { country: country.country };
      }

      const countryNode = { country: country.country };

      if (country.states && country.states.length > 0) {
        countryNode.children = country.states.map(state => {
          // Optimization: If state is fully selected, don't send children
          const origState = findOriginalState(country.country, state.state);
          if (origState && isStateFullySelected(country.country, origState)) {
            return { state: state.state };
          }

          const stateNode = { state: state.state };

          if (state.cities && state.cities.length > 0) {
            stateNode.children = state.cities.map(city => {
              // Optimization: If city is fully selected, don't send children
              const origCity = findOriginalCity(country.country, state.state, city.name);
              if (origCity && isCityFullySelected(country.country, state.state, origCity)) {
                return { city: city.name };
              }

              const cityNode = { city: city.name };

              if (city.depots && city.depots.length > 0) {
                cityNode.children = city.depots.map(depot => ({
                  depot: depot
                }));
              }

              return cityNode;
            });
          }

          return stateNode;
        });
      }

      return countryNode;
    });
  };

  /* ---------- UI ---------- */

  const filteredLocations = useMemo(
    () => filterLocations(locations, search),
    [locations, search]
  );

  // Auto-expand all nodes that contain search matches
  useEffect(() => {
    if (!search) {
      // Clearing the search restores the default expansion, not a collapsed tree.
      setExpanded(buildDefaultExpanded(filteredLocations));
      return;
    }

    const newExpanded = {};

    // Expand all nodes that contain matches
    filteredLocations.forEach((country) => {
      // Expand country level
      newExpanded[country.country] = true;

      country.states.forEach((state) => {
        // Expand state level
        const stateKey = `${country.country}-${state.state}`;
        newExpanded[stateKey] = true;

        state.cities.forEach((city) => {
          // Expand city level if it has depots
          if (city.depots.length > 0) {
            const cityKey = `${country.country}-${state.state}-${city.name}`;
            newExpanded[cityKey] = true;
          }
        });
      });
    });

    setExpanded(newExpanded);
  }, [search, filteredLocations]);

  /* Helper to check if all locations are selected */
  const isAllSelected = () => {
    if (selected.length !== locations.length) return false;
    return locations.every((country) => isCountryFullySelected(country));
  };

  const handleSelectAll = () => {
    setSelected(deepClone(locations));
  };

  const handleDeselectAll = () => {
    setSelected([]);
  };

  const handleOnApply = () => {
    let transformedSelection;
    if (isAllSelected()) {
      transformedSelection = [];
    } else {
      transformedSelection = transformSelection(selected);
    }
    onChange(transformedSelection);
    const selectedLocationsDisplayData = getSelectedLocationsDisplayLabelOutdated({
      onboardingLocations: selected,
      allOnboardingLocations: locations
    });
    setDisplayLabel(selectedLocationsDisplayData?.displayLabel);
    setTooltipLabel(selectedLocationsDisplayData?.tooltipLabel)
    setAnchorEl(null);
  };

  useEffect(() => {
    setEnableDeselectAllOption(selected.length > 0);
    setEnableSelectAllOption(!isAllSelected());
  }, [selected]);

  useEffect(() => {
    if (open) {
      if (selectedLocations) {
        setSelected(convertFromTransformedFormat(selectedLocations, locations));
      } else {
        setSelected(deepClone(locations));
      }
    }
  }, [open, selectedLocations, locations]);

  useEffect(() => {
    setExpanded(() => buildDefaultExpanded(locations));
    if (selectedLocations) {
      setSelected(() => convertFromTransformedFormat(selectedLocations, locations));
      // Update display label based on selected locations
      const selectedLocationsDisplayData = getSelectedLocationsDisplayLabelOutdated({
        onboardingLocations: convertFromTransformedFormat(selectedLocations, locations),
        allOnboardingLocations: locations
      });
      setDisplayLabel(selectedLocationsDisplayData?.displayLabel);
      setTooltipLabel(selectedLocationsDisplayData?.tooltipLabel);
    } else {
      setSelected(() => deepClone(locations));
    }
  }, [locations, selectedLocations]);

  return (
    <>
      <PrimaryTooltip title={tooltipLabel}>
        <Box>
          <FilterSelectBox
            open={open}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            label="Operating Location"
            displayValue={displayLabel}
          />
        </Box>
      </PrimaryTooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: popoverPaperStyles } }}
      >
        <Grid2
          container
          flexDirection={"column"}
          gap={2}
          sx={{ width: "315px" }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="fw700s14lh24c3D4B5A">
              Select Operating Locations
            </Typography>
            <Box>
              <Typography
                variant="fw500s12lh24c6D72F6"
                sx={{
                  cursor: enableSelectAllOption ? "pointer" : "not-allowed",
                  color: enableSelectAllOption ? "#6D72F6" : "#8F8F8F",
                }}
                onClick={() => enableSelectAllOption ? handleSelectAll() : null}
              >
                {t("actions.selectAll")}
              </Typography>
            </Box>
          </Box>

          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={"Search Location"}
          />

          <Box sx={{ height: "200px", overflowY: "auto" }}>
            <List disablePadding>
              {filteredLocations.map((country) => {
                const key = country.country;
                const checked = isCountryFullySelected(country);
                const indeterminate = isCountryPartiallySelected(country);
                return (
                  <React.Fragment key={key}>
                    <StyledListItem dense>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(key)}
                        sx={{ p: 0 }}
                      >
                        <ExpandCollapseIcon isExpanded={expanded[key]} />
                      </IconButton>
                      <ListItemIcon sx={{ minWidth: "auto" }}>
                        <StyledFormControlLabel
                          hasChildren={country.states.length > 0}
                          isLastNode={false}
                          control={
                            <CustomCheckbox
                              checked={checked}
                              indeterminate={indeterminate}
                              onChange={() => toggleCountry(country.country)}
                            />
                          }
                          label={
                            <Typography
                              variant="fw400s12c494949"
                              sx={{ lineHeight: "24px" }}
                            >
                              {country.country}
                            </Typography>
                          }
                        />
                      </ListItemIcon>
                    </StyledListItem>
                    <Collapse in={expanded[key]} timeout="auto" unmountOnExit>
                      <List disablePadding>{renderStates(country)}</List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5 }}>
            <Box>
              <Typography
                variant="fw500s12lh24c6D72F6"
                sx={{
                  cursor: enableDeselectAllOption ? "pointer" : "not-allowed",
                  color: enableDeselectAllOption ? "#6D72F6" : "#8F8F8F",
                }}
                onClick={() => enableDeselectAllOption ? handleDeselectAll() : null}
              >
                {t("actions.deselectAll")}
              </Typography>
            </Box>
            <PrimaryFormActionButton
              onClick={handleOnApply}
              disabled={selected.length === 0}
            >
              Apply
            </PrimaryFormActionButton>
          </Box>
        </Grid2>
      </Popover>
    </>
  );
};

export default OnboardingLocationFilter;
